import type { Tab } from "../features/shell/navigation.js";
import { dispatchFeatureRefresh, type FeatureRefreshTarget } from "./refreshEvents.js";

/**
 * Who owns a self-fetching tab's FIRST load (#1501).
 *
 * Six tabs render a view that fetches for itself: it loads in a mount effect
 * and subscribes to its own scope with `addFeatureRefreshListener`. The shell
 * holds none of their rows, so `refreshActiveTab` re-reads them by dispatching
 * that scope rather than by calling a loader of its own.
 *
 * That left arrival with two owners. `App`'s tab effect called
 * `refreshActiveTab`, which dispatched the scope, at the same moment the view
 * that had just mounted ran its own load — two full loads per arrival, doubled
 * again by StrictMode in dev. Whether a module paid looked like timing luck:
 * child effects commit before the parent's, so whether the listener was
 * subscribed when the dispatch went out depended on where StrictMode's cleanup
 * pass fell. Measured off the wire on one dev page load, `/garden` did four
 * loads and `/network` two, from identical code shapes.
 *
 * The rule this module exists to hold, chosen because it is what
 * `refreshActiveTab` is FOR — re-reading what is already on screen, after a
 * write, a realtime echo, or a manual refresh:
 *
 *   **A view that fetches for itself owns its first load. Arriving at its tab
 *   is a MOUNT, not a refresh, so the shell must not dispatch there.**
 *
 * The other direction — drop the mount load and let `App`'s dispatch be the
 * first load — was rejected: it makes a module's data depend on a parent effect
 * it cannot see, and a view rendered anywhere else would never load at all.
 *
 * Arrival is therefore `loadTabData` in `App.tsx` (shell-owned loaders only,
 * no dispatch); every other refresh path is `refreshActiveTab`, which is
 * `loadTabData` plus exactly one `dispatchSelfFetchingRefresh`. A new
 * self-fetching module inherits that by adding a row here, so it cannot
 * re-inherit the race by copying the effect shape.
 *
 * Keys are `Tab` ids, values are refresh scopes; the two vocabularies differ
 * (`floor-plan` the tab, `floorPlan` the scope), which is the other reason this
 * mapping is written down once instead of re-derived at each call site.
 */
const selfFetchingTabScopes = {
  projects: "projects",
  "floor-plan": "floorPlan",
  garden: "garden",
  bms: "bms",
  network: "network",
  feedback: "feedback",
  // #1677: the home's card pages are its own; the shell's `loadRecipes` on this
  // tab reads only the meal plan and the lists.
  recipes: "recipes"
} as const satisfies Partial<Record<Tab, FeatureRefreshTarget>>;

/** The tabs above, for tests and for anything that needs to enumerate them. */
export const selfFetchingTabs = Object.keys(selfFetchingTabScopes) as (keyof typeof selfFetchingTabScopes)[];

const scopeByTab: Record<string, FeatureRefreshTarget | undefined> = selfFetchingTabScopes;

/**
 * The refresh scope a tab's own view listens on, or `null` when the shell holds
 * that tab's data itself. Takes a plain string because callers hand it either a
 * `Tab` or an already-resolved `RefreshScope`.
 */
export function selfFetchingScopeForTab(tab: string): FeatureRefreshTarget | null {
  return scopeByTab[tab] ?? null;
}

/**
 * Ask the view on this tab to re-read, if it is one that fetches for itself.
 *
 * Never called on arrival — see the rule above. A no-op for every tab whose
 * data the shell loads, which is what lets `refreshActiveTab` call it
 * unconditionally.
 */
export function dispatchSelfFetchingRefresh(tab: string): void {
  const scope = selfFetchingScopeForTab(tab);
  if (scope) dispatchFeatureRefresh(scope);
}
